import { useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import {
  FilterBar,
  flattenTokens,
  QueryBuilder,
  useQueryBuilderContext,
} from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { FieldOptions, Operator, ParsedToken } from '@gonfalon/dataviz-experimental/types';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import type { MetricDataSource, MetricFilter } from '@gonfalon/metrics/types';
import { getSuggestions, type GetSuggestionsRequestBody } from '@gonfalon/openapi/operations/getSuggestions';
import { getMetricDataSourceByKeyQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { FieldGroup, Label } from '@launchpad-ui/components';
import { useQueries, useQuery } from '@tanstack/react-query';

import type { MetricForm } from '../../../CreateMetricModal/formUtils';
import {
  combineEventFilters,
  eventFilterToQuery,
  isSplittableAcrossBars,
  queryToEventFilter,
  splitFilterByCategory,
} from '../../../CreateMetricModal/MetricEventQueryBuilder/eventQueryConversion';
import { validateFilter } from '../MetricEventFilters/filterUtils';

import { EventFilterInfoTooltip } from './EventFilterInfoTooltip';

import styles from './WarehouseEventQueryField.module.css';

export type WarehouseFilterFieldName = 'filters' | 'denominator.filters';

interface WarehouseEventQueryFieldProps {
  context: UseFormReturn<MetricForm>;
  /** Which filter the editor is bound to. Ratio metrics render one editor per term. */
  fieldName?: WarehouseFilterFieldName;
  /** Source whose columns are offered. Defaults to the form's own `dataSourceKey`. */
  dataSourceKey?: string;
}

const VALUE_LIMIT = 100;

const WAREHOUSE_NATIVE_SUGGESTION_KIND = 'warehouse_native';

// The generated SuggestionRequest schema models only the hosted `{domain, kind, timeRange}` data
// source, so the warehouse-native body is cast at this one boundary. Contract:
// internal/experimentation/metrics/application/metric_filter_value_suggestions_handler.go
function buildFieldSuggestionsPayload(projectKey: string, dataSourceKey: string, field: string) {
  return {
    projectKey,
    dataSource: { kind: WAREHOUSE_NATIVE_SUGGESTION_KIND, dataSourceKey },
    suggestFields: { field, match: 'exact', limit: VALUE_LIMIT },
  } as unknown as GetSuggestionsRequestBody;
}

function useColumnValues(
  projectKey: string,
  dataSourceKey: string | undefined,
  columns: string[],
): Map<string, Array<{ value: string; approxCount: number }>> {
  return useQueries({
    queries: columns.map((field) => ({
      queryFn: () => getSuggestions({ body: buildFieldSuggestionsPayload(projectKey, dataSourceKey ?? '', field) }),
      queryKey: ['suggestions', WAREHOUSE_NATIVE_SUGGESTION_KIND, projectKey, dataSourceKey, field],
      enabled: Boolean(projectKey && dataSourceKey),
    })),
    combine: (results) => {
      const byField = new Map<string, Array<{ value: string; approxCount: number }>>();
      for (const result of results) {
        for (const item of result.data?.data?.items ?? []) {
          const seen = new Set<string>();
          byField.set(
            item.field,
            (item.sampleValues ?? []).flatMap((sample) => {
              const value = String(sample.value);
              if (seen.has(value)) {
                return [];
              }
              seen.add(value);
              return [{ value, approxCount: sample.count ?? 0 }];
            }),
          );
        }
      }
      return byField;
    },
  });
}

const STRING_OPERATORS: Operator[] = ['=', '!=', '**', '^=', '=^'];
const NUMERIC_OPERATORS: Operator[] = ['=', '!=', '>', '>=', '<', '<='];
const BOOLEAN_OPERATORS: Operator[] = ['=', '!='];

function toValueType(columnType: string): 'string' | 'number' | 'boolean' {
  if (/BOOL/i.test(columnType)) {
    return 'boolean';
  }
  if (/INT|NUM|FLOAT|DOUBLE|DEC|REAL|FIXED/i.test(columnType)) {
    return 'number';
  }
  return 'string';
}

const OPERATORS_BY_VALUE_TYPE = {
  string: STRING_OPERATORS,
  number: NUMERIC_OPERATORS,
  boolean: BOOLEAN_OPERATORS,
};

function toFieldOptions(dataSource: MetricDataSource | undefined): FieldOptions[] {
  return (dataSource?.columnMappings?.columns ?? []).map((column) => {
    const valueType = toValueType(column.type);
    return {
      field: column.name,
      valueType,
      allowedOperators: OPERATORS_BY_VALUE_TYPE[valueType],
      multi: valueType === 'string',
      supportsMultipleValues: valueType === 'string',
      allowCustom: true,
      sampleValues: [],
      description: `${column.type} column`,
    } satisfies FieldOptions;
  });
}

const EVENT_PROPERTY_PREFIXES = ['data_json.', 'evt.'];

function toColumnName(field: string): string {
  const prefix = EVENT_PROPERTY_PREFIXES.find((candidate) => field.startsWith(candidate));
  return prefix ? field.slice(prefix.length) : field;
}

function referencedColumns(tokens: ParsedToken[]): string[] {
  const fields = flattenTokens(tokens).flatMap((token) =>
    token.type === 'FILTER' && token.field ? [toColumnName(token.field)] : [],
  );
  return [...new Set(fields)];
}

function toColumnQuery(query?: string): string | undefined {
  if (!query) {
    return query;
  }
  return query.replace(/(^|[\s(!])(?:data_json\.|evt\.)/g, '$1');
}

const WarehouseFilterBar: React.FC<{
  columnOptions: FieldOptions[];
  dataSourceKey?: string;
  valueColumn?: string;
  inputId: string;
  labelId: string;
}> = ({ columnOptions, dataSourceKey, valueColumn, inputId, labelId }) => {
  const projectKey = useProjectKey();
  const { parsedQuery } = useQueryBuilderContext();

  const columns = useMemo(() => {
    const referenced = referencedColumns(parsedQuery);
    return [...new Set([valueColumn, ...referenced].filter((column): column is string => Boolean(column)))];
  }, [parsedQuery, valueColumn]);

  const valuesByField = useColumnValues(projectKey, dataSourceKey, columns);

  const fieldOptions = useMemo(
    () =>
      columnOptions.map((option) => {
        const values = valuesByField.get(toColumnName(option.field));
        return values?.length ? { ...option, sampleValues: values } : option;
      }),
    [columnOptions, valuesByField],
  );

  return (
    <div className={styles.queryBuilderFilters}>
      <FilterBar
        fieldOptions={fieldOptions}
        allowExistsOption={false}
        inputId={inputId}
        ariaLabelledby={labelId}
        showSearchIcon
        placeholder=""
      />
    </div>
  );
};

export const WarehouseEventQueryField: React.FC<WarehouseEventQueryFieldProps> = ({
  context,
  fieldName = 'filters',
  dataSourceKey: dataSourceKeyProp,
}) => {
  const projectKey = useProjectKey();
  const {
    control,
    formState: { errors },
  } = context;

  // Each ratio term filters against its own source, so the caller can override the form's.
  const formDataSourceKey = useWatch({ control, name: 'dataSourceKey' });
  const dataSourceKey = dataSourceKeyProp ?? formDataSourceKey;
  const { data: dataSource } = useQuery({
    ...getMetricDataSourceByKeyQuery({ projKey: projectKey, dataSourceKey: dataSourceKey as string }),
    enabled: Boolean(dataSourceKey),
  });
  const columnOptions = useMemo(() => toFieldOptions(dataSource), [dataSource]);

  // Both ratio editors mount at once, so ids have to be unique per field.
  const inputId = fieldName === 'filters' ? 'warehouseEventFilters' : 'warehouseDenominatorEventFilters';
  const labelId = `${inputId}-label`;
  const errorMessage = fieldName === 'filters' ? errors.filters?.message : errors.denominator?.filters?.message;

  const filters = useWatch({ control, name: fieldName });
  // A context-attribute filter configured against a hosted source survives the source switch. It has
  // no warehouse column to bind to and toMetricPost drops it, so keep it out of the bar and out of
  // validation, but preserve it in the stored value for a switch back — which only holds when the
  // halves re-AND to the original.
  const split = splitFilterByCategory(filters);
  const preservedContextFilter = isSplittableAcrossBars(filters) ? split.contextFilter : undefined;
  const storedQuery = split.eventFilter ? toColumnQuery(eventFilterToQuery(split.eventFilter)) : undefined;
  const editedRef = useRef(false);
  const [seed, setSeed] = useState<string | undefined>(storedQuery);

  useEffect(() => {
    if (!editedRef.current) {
      setSeed(storedQuery);
    }
  }, [storedQuery]);

  return (
    <Controller
      control={control}
      name={fieldName}
      rules={{ validate: (value) => validateFilter(splitFilterByCategory(value as MetricFilter).eventFilter) }}
      render={({ field }) => (
        <QueryBuilder
          key={seed}
          className={styles.queryBuilder}
          defaultValue={seed}
          onChange={(event) => {
            editedRef.current = true;
            field.onChange(combineEventFilters(preservedContextFilter, queryToEventFilter(event.tokens)));
          }}
        >
          <FieldGroup className={styles.filterGroup} errorMessage={errorMessage}>
            <div className={styles.filterHeader}>
              {/* `htmlFor` targets the bar's trailing free-text input (FilterBar passes `inputId`
                  through to it), so clicking the label focuses the bar — same wiring as TraceField.
                  `id` stays for the bar container's `aria-labelledby`. */}
              <Label id={labelId} htmlFor={inputId}>
                <Message id="metrics.eventFilters.filterByEvent" />
              </Label>
              <EventFilterInfoTooltip labelText={msg('metrics.eventFilters.aboutEventPropertyFilters')}>
                <Message id="metrics.eventFilters.warehouseFilterHelp" />
              </EventFilterInfoTooltip>
            </div>
            <WarehouseFilterBar
              columnOptions={columnOptions}
              dataSourceKey={dataSourceKey}
              valueColumn={dataSource?.columnMappings?.valueColumn}
              inputId={inputId}
              labelId={labelId}
            />
          </FieldGroup>
        </QueryBuilder>
      )}
    />
  );
};
